<template>
  <Teleport to=".main">
    <div class="wa-quickview" @click.self="close">
      <div class="wa-quickview-panel" role="dialog" :dir="textDirection" :aria-label="dialogLabel">
        <header class="wa-quickview-header">
          <div class="wa-quickview-titles">
            <span class="wa-quickview-eyebrow">
              {{ $t(titleKey) }}
              <template v-if="items.length"> · {{ items.length }}</template>
            </span>
            <h3 v-if="subtitle" class="wa-quickview-title" dir="auto">{{ subtitle }}</h3>
            <p v-if="hintKey" class="wa-quickview-hint">{{ $t(hintKey) }}</p>
          </div>
          <button type="button" class="wa-quickview-close" :aria-label="$t('close')" @click="close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </header>

        <div class="wa-quickview-body">
          <p v-if="summary" class="wa-quickview-meta">{{ summary }}</p>

          <ul v-if="items.length" class="wa-quickview-list">
            <li v-for="(item, idx) in items" :key="`${item}-${idx}`">
              <span class="wa-quickview-bullet" aria-hidden="true"></span>
              <span class="wa-quickview-item-text" dir="auto">{{ item }}</span>
            </li>
          </ul>
          <p v-else-if="emptyText" class="wa-quickview-empty">{{ emptyText }}</p>

          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { mapGetters } from 'vuex'
import Teleport from 'vue2-teleport'
import { lockPageScroll, unlockPageScroll } from '@/modules/common/utils/scrollLock'

export default {
  props: {
    titleKey: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    hintKey: {
      type: String,
      default: '',
    },
    summary: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    emptyText: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters(['isRTL']),
    textDirection() {
      return this.isRTL ? 'rtl' : 'ltr'
    },
    dialogLabel() {
      const title = this.$t(this.titleKey)
      return this.subtitle ? `${title} — ${this.subtitle}` : title
    },
  },
  mounted() {
    lockPageScroll()
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    unlockPageScroll()
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    close() {
      this.$emit('close')
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.close()
    },
  },
  components: { Teleport },
}
</script>

<style lang="scss" scoped>
@use "@/styles/vars.scss" as *;

.wa-quickview {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(52, 54, 77, 0.28);
  backdrop-filter: blur(2px);
  animation: wa-quickview-fade-in 0.16s ease-out;

  @media (max-width: 768px) {
    padding: 12px;
    align-items: center;
  }
}

.wa-quickview-panel {
  width: min(360px, 100%);
  max-height: min(65vh, 480px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid rgba(52, 54, 77, 0.08);
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(52, 54, 77, 0.14);
  overflow: hidden;
  animation: wa-quickview-slide-in 0.18s ease-out;

  @media (max-width: 768px) {
    width: 100%;
    max-height: min(85vh, 520px);
    border-radius: 12px;
  }
}

.wa-quickview-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 10px;

  @media (max-width: 768px) {
    padding: 12px 14px 8px;
  }
}

.wa-quickview-titles {
  min-width: 0;
}

.wa-quickview-eyebrow {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa3ad;
  line-height: 1.2;
}

.wa-quickview-title {
  margin: 4px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: $black-blue;
  line-height: 1.35;
  word-break: break-word;
  text-align: start;
}

.wa-quickview-hint {
  margin: 6px 0 0;
  font-size: 11px;
  line-height: 1.45;
  color: #a0a8b0;
  text-align: start;
}

.wa-quickview-close {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: -2px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #9aa3ad;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    background: #f4f6f8;
    color: $black-blue;
  }
}

.wa-quickview-body {
  padding-block-end: 14px;
  padding-inline: 16px 8px;
  overflow-y: auto;
  text-align: start;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding-inline: 14px 10px;
    padding-block-end: 16px;
  }
}

.wa-quickview-meta {
  margin: 0 0 10px;
  padding: 5px 10px;
  display: inline-block;
  border-radius: 6px;
  background: #f4f8fc;
  color: #3d6a99;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.wa-quickview-list {
  margin: 0;
  padding: 0;
  padding-inline-end: 8px;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 7px 0;
    font-size: 13px;
    line-height: 1.4;
    color: #3a4652;

    & + li {
      border-top: 1px solid #f1f3f5;
    }
  }
}

.wa-quickview-bullet {
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  margin-top: 7px;
  border-radius: 50%;
  background: $blue;
  opacity: 0.55;
}

.wa-quickview-item-text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.wa-quickview-empty {
  margin: 4px 0 0;
  padding: 12px 0;
  font-size: 13px;
  color: #b0b8c1;
  text-align: center;
}

@keyframes wa-quickview-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes wa-quickview-slide-in {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
